<template>
	<div ref="containerRef" class="flex items-center relative w-full">
		<form class="w-full" @submit.prevent="goToSearchPage(searchQuery)">
			<input
				v-model="searchQuery"
				:type="blok.type"
				:class="[
					blok.icon ? 'pl-10' : 'pl-4',
					`bg-graycustom w-full h-9 text-gray-super-dark placeholder:text-gray-super-dark placeholder:text-xs text-xs lg:max-w-[331px]`
				]"
				:placeholder="blok.placeholder"
				:name="blok.name"
				:required="blok.required"
				@focus="isFocused = true"
				@keydown="onInputKeydown"
			/>
		</form>
		<div
			v-if="blok.icon"
			class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
		>
			<MagnifyingGlassIcon class="h-4 w-4 text-primary" />
		</div>

		<div
			v-if="isFocused && searchQuery.trim().length >= 2"
			class="absolute left-0 top-full z-40 mt-1 w-full max-w-[331px] rounded border border-gray-200 bg-white shadow-lg"
		>
			<NuxtLink
				v-for="(item, index) in suggestions"
				:key="item.id"
				:to="item.to"
				class="flex items-center gap-3 px-3 py-2 text-sm text-gray-800 hover:bg-gray-100"
				:class="{ 'bg-gray-100': activeIndex === index }"
				@mouseenter="activeIndex = index"
				@click="openSuggestion(item)"
			>
				<div class="h-10 w-10 shrink-0 overflow-hidden rounded bg-gray-100">
					<NuxtImg
						v-if="item.image"
						:src="item.image"
						:alt="item.label"
						class="h-full w-full object-cover"
						width="40"
						height="40"
					/>
				</div>
				<div class="min-w-0">
					<p class="truncate text-sm font-medium">{{ item.label }}</p>
					<p class="text-xs uppercase text-gray-500">{{ item.kind }}</p>
				</div>
			</NuxtLink>
			<button
				type="button"
				class="block w-full border-t border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-900 hover:bg-gray-100"
				@click="goToSearchPage(searchQuery)"
			>
				Search for "{{ searchQuery.trim() }}"
			</button>
			<p v-if="isLoadingSuggestions" class="px-3 py-2 text-xs text-gray-500">
				Loading suggestions...
			</p>
			<p
				v-if="!isLoadingSuggestions && suggestions.length === 0"
				class="px-3 py-2 text-xs text-gray-500"
			>
				No suggestions found
			</p>
		</div>
	</div>
</template>

<script setup lang="ts">
import { MagnifyingGlassIcon } from "@heroicons/vue/24/solid";
import type { BlokInput } from "~/types/storyblok/storyblok";
import type { SearchResult } from "@nwt/norce-pwa/dist/runtime/types/api/searchEngine/searchResult";
import type { HelloRetailSearch } from "@nwt/norce-pwa/dist/runtime/types/api/helloRetail/helloRetailSearch";
import { useRoute } from "nuxt/app";

interface Props {
	blok: BlokInput;
}

interface SuggestionItem {
	id: string;
	label: string;
	to: string;
	image?: string;
	kind: "product" | "article";
}

const { blok } = defineProps<Props>();
const localePath = useLocalePath();
const route = useRoute();
const { localeProperties } = useI18n();
const searchQuery = ref<string>((route.query.q as string) || "");
const suggestions = ref<SuggestionItem[]>([]);
const isFocused = ref(false);
const isLoadingSuggestions = ref(false);
const activeIndex = ref(-1);
const containerRef = ref<HTMLElement | null>(null);

const toPath = (value?: string) => {
	if (!value) {
		return "/";
	}
	if (value.startsWith("http://") || value.startsWith("https://")) {
		try {
			const url = new URL(value);
			return `${url.pathname}${url.search}`;
		} catch {
			return "/";
		}
	}
	return value.startsWith("/") ? value : `/${value}`;
};

const mapSuggestionsFromSearch = (
	data: SearchResult[] | HelloRetailSearch | null
): SuggestionItem[] => {
	if (!data) {
		return [];
	}

	if (Array.isArray(data)) {
		return data.slice(0, 6).map((item, index) => {
			const meiliItem = item as {
				type?: string;
				partNo?: string;
				names?: Record<string, { name?: string; uniqueUrlName?: string }>;
				image?: string;
				slug?: string;
				name?: string;
				title?: string;
				url?: string;
				uid?: string;
				hierarchy_radio_lvl1?: string;
				hierarchy_lvl1?: string;
			};

			if (meiliItem.type?.toLowerCase() === "product") {
				const localeKey = localeProperties.value.iso || "sv-SE";
				const itemName = meiliItem.names?.[localeKey]?.name || meiliItem.partNo;
				const uniqueUrlName = meiliItem.names?.[localeKey]?.uniqueUrlName;
				return {
					id: `${meiliItem.type}-${meiliItem.partNo}-${index}`,
					label: itemName || "Product",
					to: uniqueUrlName ? localePath(`/${uniqueUrlName}`) : "/",
					image: meiliItem.image
						? `${meiliItem.image}?mode=max&format=webp&w=80`
						: undefined,
					kind: "product"
				};
			}

			// Fallback for generic records (e.g. indexed page docs with url/content/hierarchy fields).
			if (
				meiliItem.url ||
				meiliItem.hierarchy_lvl1 ||
				meiliItem.hierarchy_radio_lvl1
			) {
				return {
					id: String(meiliItem.uid || meiliItem.url || index),
					label:
						meiliItem.hierarchy_radio_lvl1 ||
						meiliItem.hierarchy_lvl1 ||
						meiliItem.name ||
						meiliItem.title ||
						"Result",
					to: toPath(meiliItem.url),
					image: meiliItem.image || undefined,
					kind: "article"
				};
			}

			return {
				id: `${meiliItem.type || "item"}-${meiliItem.slug || index}`,
				label: meiliItem.name || meiliItem.title || "Article",
				to: toPath(meiliItem.slug || meiliItem.url),
				image: meiliItem.image ? `https:${meiliItem.image}` : undefined,
				kind: "article"
			};
		});
	}

	const helloResults = data.products?.results || [];
	return helloResults.slice(0, 6).map((item, index) => ({
		id: `hello-${item.productNumber}-${index}`,
		label: item.title || String(item.productNumber),
		to: toPath(item.url),
		image: item.imgUrl || undefined,
		kind: "product"
	}));
};

const goToSearchPage = (query: string) => {
	const normalized = query.trim();
	if (!normalized) {
		return;
	}
	isFocused.value = false;
	activeIndex.value = -1;
	navigateTo(
		localePath({
			path: "/search",
			query: { q: normalized }
		})
	);
};

const openSuggestion = (item: SuggestionItem) => {
	isFocused.value = false;
	activeIndex.value = -1;
	navigateTo(item.to);
};

const onInputKeydown = (event: KeyboardEvent) => {
	if (!isFocused.value || suggestions.value.length === 0) {
		if (event.key === "Escape") {
			isFocused.value = false;
		}
		return;
	}

	if (event.key === "ArrowDown") {
		event.preventDefault();
		activeIndex.value = (activeIndex.value + 1) % suggestions.value.length;
		return;
	}

	if (event.key === "ArrowUp") {
		event.preventDefault();
		activeIndex.value =
			(activeIndex.value - 1 + suggestions.value.length) %
			suggestions.value.length;
		return;
	}

	if (event.key === "Enter" && activeIndex.value >= 0) {
		event.preventDefault();
		openSuggestion(suggestions.value[activeIndex.value]!);
		return;
	}

	if (event.key === "Escape") {
		event.preventDefault();
		isFocused.value = false;
		activeIndex.value = -1;
	}
};

let debounceTimer: ReturnType<typeof setTimeout> | undefined;
watch(searchQuery, (value) => {
	if (debounceTimer) {
		clearTimeout(debounceTimer);
	}

	if (value.trim().length < 2) {
		suggestions.value = [];
		activeIndex.value = -1;
		return;
	}

	debounceTimer = setTimeout(async () => {
		isLoadingSuggestions.value = true;
		try {
			const result = await $fetch<SearchResult[] | HelloRetailSearch>(
				`/api/search-proxy?query=${encodeURIComponent(value.trim())}&start=0`,
				{ method: "GET" }
			);
			suggestions.value = mapSuggestionsFromSearch(result).filter(
				(item) => item.to && item.to !== "/"
			);
			activeIndex.value = suggestions.value.length > 0 ? 0 : -1;
		} catch {
			suggestions.value = [];
			activeIndex.value = -1;
		} finally {
			isLoadingSuggestions.value = false;
		}
	}, 180);
});

watch(
	() => route.query.q,
	(newQuery) => {
		searchQuery.value = (newQuery as string) || "";
		activeIndex.value = -1;
	}
);

onMounted(() => {
	document.addEventListener("click", onClickOutside);
});

onBeforeUnmount(() => {
	document.removeEventListener("click", onClickOutside);
});

const onClickOutside = (event: MouseEvent) => {
	if (!containerRef.value) {
		return;
	}
	if (!containerRef.value.contains(event.target as Node)) {
		isFocused.value = false;
		activeIndex.value = -1;
	}
};
</script>
